import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

export const HUB_TABS = [
  { key: "open", label: "Em aberto" },
  { key: "accepted", label: "Aceitos" },
  { key: "finished", label: "Finalizados" },
  { key: "cancelled", label: "Cancelados" },
];

export const EMPTY_QUEUE_HUB_FILTERS = {
  number: "",
  origin: "",
  destination: "",
  uidShipperFilter: "",
  tab: "open",
  onlyWithQueue: false,
};

export function buildHubQueryParams(filters, user) {
  const tab = filters.tab || "open";
  const params = {
    tab,
    onlyWithQueue: filters.onlyWithQueue === true,
  };

  if (filters.number?.trim()) params.numberSerial = filters.number.trim();
  // origem/destino: filtrados no front pela rota exibida (origin/destination).
  // O summary nem sempre aplica origem/destino no mesmo formato da coluna Rota.
  if (filters.uidShipperFilter?.trim()) {
    params.uidShipperFilter = filters.uidShipperFilter.trim();
  }

  if (user?.uid) params.idUser = user.uid;

  if (user?.viewALL === true) {
    params.viewAll = "true";
  } else {
    if (user?.uidShipper) params.uidShipper = user.uidShipper;
    if (typeof user?.viewALL === "boolean") {
      params.viewAll = String(user.viewALL);
    }
  }

  if (Array.isArray(user?.uidShipperAffiliates) && user.uidShipperAffiliates.length) {
    params.affiliates = user.uidShipperAffiliates.join(",");
  }

  return params;
}

/** Normaliza texto de rota para comparar filtro x item listado. */
export function normalizeRouteText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s*[-–—]\s*/g, "/")
    .replace(/\s*\/\s*/g, "/")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Compara filtro de origem/destino com a rota exibida na tabela.
 * Aceita "Cidade - UF", "Cidade/UF" e busca parcial pelo nome da cidade.
 */
export function matchesRouteFilter(routeValue, filterValue) {
  const filter = normalizeRouteText(filterValue);
  if (!filter) return true;

  const route = normalizeRouteText(routeValue);
  if (!route) return false;

  if (route.includes(filter) || filter.includes(route)) return true;

  const [filterCity, filterUf = ""] = filter.split("/").map((part) => part.trim());
  const [routeCity, routeUf = ""] = route.split("/").map((part) => part.trim());

  if (!filterCity || !routeCity) return false;

  const cityMatches =
    routeCity.includes(filterCity) || filterCity.includes(routeCity);

  if (!cityMatches) return false;
  if (filterUf && routeUf) return filterUf === routeUf;
  return true;
}

export function filterFreightGroupsByRoute(groups, filters = {}) {
  const list = Array.isArray(groups) ? groups : [];
  const origin = filters.origin || "";
  const destination = filters.destination || "";

  if (!origin.trim() && !destination.trim()) return list;

  return list.filter(
    (freight) =>
      matchesRouteFilter(freight?.origin, origin) &&
      matchesRouteFilter(freight?.destination, destination),
  );
}

function mapAcceptedCarrier(freight) {
  const raw =
    freight?.acceptedCarrier ||
    freight?.acceptedTransport ||
    freight?.transport ||
    freight?.carrier ||
    null;

  if (!raw || typeof raw !== "object") {
    const nameOnly =
      freight?.acceptedCarrierName ||
      freight?.nameCarrier ||
      freight?.transportName ||
      null;
    if (!nameOnly) return null;
    return {
      idCarrier: freight?.idCarrier || freight?.idTransport || null,
      nameCarrier: String(nameOnly),
    };
  }

  const idCarrier =
    raw.idCarrier ||
    raw.idTransport ||
    raw.uid ||
    raw.id ||
    null;
  const nameCarrier =
    raw.nameCarrier ||
    raw.name ||
    raw.fantasyName ||
    raw.companyName ||
    raw.razaoSocial ||
    null;

  if (!idCarrier && !nameCarrier) return null;

  return {
    idCarrier: idCarrier ? String(idCarrier) : null,
    nameCarrier: nameCarrier ? String(nameCarrier) : "—",
  };
}

function formatHubLocation(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (typeof value !== "object") return String(value);

  const city =
    value.city ||
    value.cidade ||
    value.xMunCarrega ||
    value.name ||
    value.nome ||
    "";
  const uf =
    value.uf ||
    value.UF ||
    value.state ||
    value.estado ||
    "";

  if (city && uf) return `${city}/${uf}`;
  if (city) return String(city);
  if (value.origin) return formatHubLocation(value.origin);
  if (value.destination) return formatHubLocation(value.destination);
  return "";
}

function mapCandidate(freightId, candidate) {
  return {
    rowKey: `${freightId}_${candidate.idCarrier}`,
    idCarrier: candidate.idCarrier,
    nameCarrier: candidate.nameCarrier,
    cnpj: candidate.cnpj ?? null,
    registeredAt: candidate.registeredAt,
    type: candidate.type || "Transportadora",
    rating:
      typeof candidate.rating === "number" ? candidate.rating : null,
    paymentTermDays: candidate.paymentTermDays ?? null,
    paymentTermLabel: candidate.paymentTermLabel ?? null,
    documentationStatus: candidate.documentationStatus || null,
    candidateStatus: candidate.candidateStatus ?? null,
    candidateStatusLabel: candidate.candidateStatusLabel ?? null,
    bidAmount: candidate.bidAmount ?? null,
    counterAmount: candidate.counterAmount ?? null,
    replyAmount: candidate.replyAmount ?? null,
    proposedAmount: candidate.proposedAmount ?? null,
    negotiationTurn: candidate.negotiationTurn ?? null,
    canAccept: candidate.canAccept !== false,
    canCounter: candidate.canCounter === true,
    canReject: candidate.canReject === true,
  };
}

export function mapTransportQueueHubFreights(hubResponse) {
  const freights = Array.isArray(hubResponse?.freights) ? hubResponse.freights : [];

  return freights
    .map((freight) => {
      const queue = (Array.isArray(freight.queue) ? freight.queue : [])
        .map((candidate) => mapCandidate(freight.freightId, candidate))
        .sort(
          (a, b) =>
            new Date(a.registeredAt || 0) - new Date(b.registeredAt || 0),
        );

      const queueCount =
        typeof freight.queueCount === "number" ? freight.queueCount : queue.length;
      const acceptedCarrier = mapAcceptedCarrier(freight);

      return {
        freightId: freight.freightId,
        numberSerial: freight.numberSerial,
        origin: formatHubLocation(freight.origin ?? freight.origem),
        destination: formatHubLocation(
          freight.destination ?? freight.destino,
        ),
        collectionDate: freight.collectionDate,
        freightValue: freight.freightValue,
        shipperUid: freight.shipperUid,
        shipperName: freight.shipperName,
        status: freight.status,
        statusUiLabel:
          freight.status?.uiLabel ||
          freight.status?.describe ||
          "—",
        acceptedCarrier,
        acceptedCarrierName: acceptedCarrier?.nameCarrier || null,
        queueCount,
        queue,
        contractingType: freight.contractingType || null,
        contractingLabel: freight.contractingLabel || null,
        paymentTermLabel: freight.paymentTermLabel || null,
        bestProposedAmount:
          typeof freight.bestProposedAmount === "number"
            ? freight.bestProposedAmount
            : null,
        oldestRegisteredAt: queue[0]?.registeredAt ?? null,
      };
    })
    .sort((a, b) => {
      const dateA = new Date(a.oldestRegisteredAt || a.collectionDate || 0);
      const dateB = new Date(b.oldestRegisteredAt || b.collectionDate || 0);
      return dateA - dateB;
    });
}

export function mapShippersFromHub(hubResponse) {
  const shippers = Array.isArray(hubResponse?.shippers) ? hubResponse.shippers : [];
  return shippers
    .map((item) => ({
      uid: item.uid || item.id || "",
      name: item.name || item.shipperName || "",
    }))
    .filter((item) => item.uid && item.name);
}

export function getHubPaginationTotal(hubResponse, fallbackCount = 0) {
  const total = hubResponse?.pagination?.total;
  if (typeof total === "number" && Number.isFinite(total)) return total;
  return fallbackCount;
}

/** @deprecated Prefer mapTransportQueueHubFreights for grouped UI */
export function flattenTransportQueueHubRows(hubResponse) {
  const freights = Array.isArray(hubResponse?.freights) ? hubResponse.freights : [];
  const rows = [];

  freights.forEach((freight) => {
    const queue = Array.isArray(freight.queue) ? freight.queue : [];
    queue.forEach((candidate) => {
      rows.push({
        rowKey: `${freight.freightId}_${candidate.idCarrier}`,
        freightId: freight.freightId,
        numberSerial: freight.numberSerial,
        origin: freight.origin,
        destination: freight.destination,
        collectionDate: freight.collectionDate,
        freightValue: freight.freightValue,
        shipperUid: freight.shipperUid,
        shipperName: freight.shipperName,
        status: freight.status,
        idCarrier: candidate.idCarrier,
        nameCarrier: candidate.nameCarrier,
        cnpj: candidate.cnpj,
        registeredAt: candidate.registeredAt,
      });
    });
  });

  return rows.sort(
    (a, b) => new Date(a.registeredAt || 0) - new Date(b.registeredAt || 0),
  );
}

export function groupRowsByFreight(rows) {
  const map = new Map();

  rows.forEach((row) => {
    if (!map.has(row.freightId)) {
      map.set(row.freightId, {
        freightId: row.freightId,
        numberSerial: row.numberSerial,
        shipperUid: row.shipperUid,
        queue: [],
      });
    }
    const group = map.get(row.freightId);
    group.queue.push({
      idTransport: row.idCarrier,
      name: row.nameCarrier,
      cnpj: row.cnpj,
      registeredAt: row.registeredAt,
    });
  });

  return [...map.values()];
}

export function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatDateBr(dateStr) {
  if (!dateStr) return "—";
  if (String(dateStr).includes("/")) return dateStr;
  const [year, month, day] = String(dateStr).split("-");
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

export function formatWaitingTime(registeredAt) {
  if (!registeredAt) return "—";
  try {
    const date = new Date(registeredAt);
    if (Number.isNaN(date.getTime())) return "—";
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch {
    return "—";
  }
}

export function formatOldestWaiting(minutes) {
  if (!minutes || minutes <= 0) return "—";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours}h ${rest}min` : `${hours}h`;
}

export function formatNullable(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

export function formatDocumentationStatus(status) {
  if (!status) return "—";
  const map = {
    ok: "Completa",
    completa: "Completa",
    complete: "Completa",
    pending: "Pendente",
    incomplete: "Incompleta",
  };
  return map[String(status).toLowerCase()] || status;
}

export function formatCandidateStatus(status, label) {
  if (label) return label;
  if (!status) return "—";
  const map = {
    waiting: "Na fila",
    in_review: "Aguardando resposta",
    rejected: "Rejeitado",
    accepted: "Aceito",
  };
  return map[status] || status;
}

export function formatCarrierType(type) {
  if (!type) return "Transportadora";
  if (type === "Autonomo" || type === "Autônomo") return "Autônomo";
  if (String(type).toLowerCase() === "transportadora") return "Transportadora";
  return type;
}

function parseRating(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.max(0, Math.min(5, num));
}

function extractPaymentTermLabel(shipper) {
  const financial = shipper?.financial || {};
  const fromArray = Array.isArray(financial.paymentConditionDescriptions)
    ? financial.paymentConditionDescriptions.find(Boolean)
    : null;
  const label =
    financial.paymentConditionDescription ||
    fromArray ||
    financial.paymentTermLabel ||
    financial.prazoPagamento ||
    shipper?.paymentConditionDescription ||
    null;
  return label ? String(label) : null;
}

function extractPaymentTermDays(shipper) {
  const financial = shipper?.financial || {};
  const days = Number(
    financial.paymentTermDays ??
      financial.prazoDias ??
      financial.days ??
      shipper?.paymentTermDays,
  );
  return Number.isFinite(days) && days > 0 ? days : null;
}

function extractCarrierRating(shipper) {
  return (
    parseRating(shipper?.rating) ??
    parseRating(shipper?.averageRating) ??
    parseRating(shipper?.ratingAverage) ??
    parseRating(shipper?.score) ??
    parseRating(shipper?.avaliacao) ??
    parseRating(shipper?.evaluation?.average) ??
    null
  );
}

function hasCompleteDocumentation(shipper) {
  const cert = shipper?.infoCertificate?.certificate;
  const docNumber = shipper?.dataPersonal?.documentNumber;
  if (cert) return true;
  if (docNumber) return true;
  return false;
}

/**
 * Extrai campos de exibição a partir do cadastro (Firestore shipper).
 */
export function extractCarrierDisplayFields(shipper) {
  if (!shipper || typeof shipper !== "object") {
    return {
      type: "Transportadora",
      rating: 2.5,
      paymentTermDays: null,
      paymentTermLabel: null,
      documentationStatus: "ok",
    };
  }

  const isTransport =
    shipper.isTransport === true ||
    shipper.isTransport === 1 ||
    String(shipper.isTransport).toLowerCase() === "true";

  const paymentTermLabel = extractPaymentTermLabel(shipper);
  const paymentTermDays = extractPaymentTermDays(shipper);

  return {
    type: isTransport ? "Transportadora" : "Autonomo",
    rating: extractCarrierRating(shipper) ?? 2.5,
    paymentTermDays,
    paymentTermLabel:
      paymentTermLabel ||
      (paymentTermDays != null ? `${paymentTermDays} dias` : null),
    documentationStatus: hasCompleteDocumentation(shipper) ? "ok" : "ok",
  };
}

/**
 * Mescla dados do cadastro nos candidatos da fila (quando a API manda null).
 */
export function enrichFreightGroupsWithCarrierProfiles(groups, profilesById = {}) {
  if (!Array.isArray(groups)) return [];

  return groups.map((freight) => ({
    ...freight,
    queue: (freight.queue || []).map((candidate) => {
      const profile = profilesById[candidate.idCarrier];
      const fromCadastro = extractCarrierDisplayFields(profile);
      const shipperPaymentTermLabel =
        freight.paymentTermLabel || candidate.paymentTermLabel || null;

      return {
        ...candidate,
        type: candidate.type || fromCadastro.type,
        rating:
          typeof candidate.rating === "number"
            ? candidate.rating
            : fromCadastro.rating,
        paymentTermDays: candidate.paymentTermDays ?? null,
        paymentTermLabel:
          shipperPaymentTermLabel ||
          (freight.contractingType === "BID"
            ? null
            : fromCadastro.paymentTermLabel),
        documentationStatus:
          candidate.documentationStatus || fromCadastro.documentationStatus,
      };
    }),
  }));
}
